import { useMemo } from 'react';
import CheckIcon from '@mui/icons-material/Check';
import ClearIcon from '@mui/icons-material/Clear';
import { Checkbox, Divider, FormControlLabel, FormGroup, IconButton } from '@mui/material';
import { partial } from 'lodash';

import { PayoutMethod } from '~api/model/payout-cycle';

import { message } from '~helper/_common';

import { useFormInputValue } from '~hooks/useFormInputValue';

import { FilterButton } from '~components/molecules/FilterButton';
import { ComponentCommonProps } from '~components/types';

import style from './PayoutMethodFilter.module.scss';

export type PayoutMethodFilterProps = ComponentCommonProps & {
    value?: string | null;
    onChange: (value: string | null) => void;
}

export const PayoutMethodFilter: React.FC<PayoutMethodFilterProps> = ({
    value,
    onChange,
    'data-test': dataTest = 'payoutMethodFilter'
}) => {
    const defaultValue = Object.values(PayoutMethod).join(',');
    const { value: filterValue, setValue: setFilterValue } = useFormInputValue(value || defaultValue);
    const handleApplyButtonClick = (requestClose: () => void) => {
        onChange(filterValue || null);
        requestClose();
    };
    const handleCancelButtonClick = (requestClose: () => void) => {
        onChange(null);
        setFilterValue(defaultValue);
        requestClose();
    };

    const checkedPayoutMethods = useMemo(() => {
        return new Set<PayoutMethod>(((filterValue)?.split(',') as PayoutMethod[] ?? []).filter(Boolean));
    }, [ filterValue ]);

    const handlePayoutMethodClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        const payoutMethod = event.target.name as PayoutMethod;

        if (event.target.checked) {
            checkedPayoutMethods.add(payoutMethod);
        } else {
            checkedPayoutMethods.delete(payoutMethod);
        }

        const newFilterValue = [ ...checkedPayoutMethods ].join(',');

        setFilterValue(newFilterValue);
        onChange(newFilterValue);
    };

    return (
        <FilterButton
            value={ value }
            data-test={ dataTest }
            render={ ({ requestClose }) => (
                <div className={ style.payoutMethods } data-test={ dataTest }>
                    <FormGroup>
                        { Object.values(PayoutMethod).map(payoutMethod => (
                            <FormControlLabel
                                key={ payoutMethod }
                                control={
                                    <Checkbox
                                        name={ payoutMethod }
                                        checked={ checkedPayoutMethods.has(payoutMethod) }
                                        onChange={ handlePayoutMethodClick }
                                        data-test={ `${dataTest}.checkbox.${payoutMethod}` }
                                    />
                                }
                                label={ message(`creator.table.filter.payoutMethod.label.${payoutMethod}`) }
                            />
                        )) }
                    </FormGroup>
                    <div className={ style.actions }>
                        <IconButton
                            size="small"
                            onClick={ partial(handleCancelButtonClick, requestClose) }
                            data-test={ `${dataTest}.cancel` }
                        >
                            <ClearIcon color="secondary" />
                        </IconButton>
                        <Divider sx={ { height: 28, m: 0.5 } } orientation="vertical" />
                        <IconButton
                            size="small"
                            onClick={ partial(handleApplyButtonClick, requestClose) }
                            data-test={ `${dataTest}.apply` }
                        >
                            <CheckIcon color="primary" />
                        </IconButton>
                    </div>
                </div>
            ) }
        />
    );
};
